import React, { useState, useEffect } from "react"
import { get, post, put, remove } from "../../utils/apiClient"
import toast from "react-hot-toast"
import Pagination from "../common/pagination"
import {
  validateFaxNumberOnlyDigit,
  formatFaxNumber,
  prepareFinalFaxNumber,
} from "../../utils/common"
import { SquarePen, Trash2, X, FileSpreadsheet, TriangleAlert } from "lucide-react"
import Select from "react-select"
import SearchInputWrapper from "../common/searchInputWrapper"

const QueueToFaxNumberAssociation = ({ reloadQueues }) => {
  const [queues, setQueues] = useState([])
  const [showPopup, setShowPopup] = useState(false)
  const [queueToEdit, setQueueToEdit] = useState(null)
  const [currentPage, setCurrentPage] = useState(1)
  const [loading, setLoading] = useState(false)
  const [actionLoading, setActionLoading] = useState(false)
  const [queueIdToDelete, setQueueIdToDelete] = useState(null)
  const [rowsPerPage, setRowsPerPage] = useState(10)
  const [searchText, setSearchText] = useState("")
  const [activeRowId, setActiveRowId] = useState(null)

  // Search Filter
  const filteredQueues = queues?.filter((queue) => {
    return Object.entries(queue).some(([key, value]) => {
      if (key === "associations" && Array.isArray(value)) {
        const newSearchText = searchText
          .replace(/[+-]/g, "")
          .trim()
          .toLowerCase()

        return value.some((assoc) => {
          const fax = assoc?.faxNumber
          if (!fax) return false

          const newFax = fax.replace(/[+-]/g, "").trim().toLowerCase()
          return newFax.includes(newSearchText)
        })
      }

      if (key === "queueName") {
        return value
          ?.toString()
          .toLowerCase()
          .includes(searchText.toLowerCase())
      }

      return false
    })
  })

  const startIdx = (currentPage - 1) * rowsPerPage
  const currentQueues = filteredQueues.slice(startIdx, startIdx + rowsPerPage)
  const totalPages = Math.ceil(filteredQueues.length / rowsPerPage)

  // const currentFaxes = sortedFaxes.slice(startIdx, startIdx + rowsPerPage)
  // const totalPages = Math.ceil(filteredFaxes.length / rowsPerPage)

  useEffect(() => {
    loadQueue()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const fetchQueues = async () => {
    try {
      const data = await get("/admin/queue-fax-number-associations")

      // const queues = data.flatMap((item) =>
      //   item.associations.map((assoc) => ({
      //     queueName: item.queueName,
      //     ...assoc,
      //   })),
      // )

      setQueues(data)
    } catch (error) {
      console.error("Failed to fetch queues", error)
    }
  }

  const loadQueue = async () => {
    setLoading(true)
    await fetchQueues()
    setLoading(false)
  }

  const handleAddQueue = async (data) => {
    setActionLoading(true)

    const isEdit = data?.isEdit

    try {
      if (isEdit) {
        await put(`/admin/queue-fax-number-association`, {
          // associationId: data.associationId,
          oldQueueName: data.oldQueueName,
          newQueueName: data.newQueueName,
          // queueOldName: data.queueOldName,
          // queueNewName: data.queueNewName,
        })
      } else {
        await post("/admin/queue-fax-number-association", {
          queueName: data.queueName,
          faxNumber: data.faxNumber,
        })
      }

      fetchQueues()
      reloadQueues() // Reload Left panel queues
      setShowPopup(false)
      setQueueToEdit(null)
      toast.success(`Queue ${isEdit ? "updated" : "added"} successfully`)
    } catch (error) {
      if (
        error ===
        "The Fax Number does not exist or does not belong to the customer."
      ) {
        toast.error(
          "The fax number provided is not associated with the specified Custom Fax Manager Application ID.",
        )
      } else {
        toast.error(error)
      }
    } finally {
      setActionLoading(false)
    }
  }

  const handleDeleteQueue = async () => {
    setActionLoading(true)
    try {
      // await remove(
      //   `/admin/queue-fax-number-association/${queueIdToDelete.associationId}`,
      // )
      if (queueIdToDelete.deleteFax) {
        await remove(
          `/admin/queue-fax-number-association/${queueIdToDelete.queueName}/fax-number/${queueIdToDelete.faxNumber}`,
        )
      } else {
        await remove(
          `/admin/queue-fax-number-association/${queueIdToDelete.queueName}`,
        )
      }
      fetchQueues()
      reloadQueues() // Reload Left panel queues
      setQueueIdToDelete(null)
      toast.success(
        `${queueIdToDelete.deleteFax ? "Fax" : "Queue"} removed successfully`,
      )
    } catch (error) {
      console.error("Failed to delete", error)
      toast.error("Failed to delete")
    } finally {
      setActionLoading(false)
    }
  }

  const handleExportCsv = () => {
    const headers = ["Queue Name", "Fax Number"]
    // Build rows
    const rows = filteredQueues.map((queue) => [
      queue.queueName,
      queue.associations.map((ass) => ass.faxNumber).join(","),
    ])
    // Assemble CSV string
    const csvContent = [headers, ...rows]
      .map((row) =>
        row
          .map(
            (cell) =>
              // escape quotes
              `"${String(cell).replace(/"/g, '""')}"`,
          )
          .join(","),
      )
      .join("\n")
    const blob = new Blob([csvContent], { type: "text/csv" })
    const url = URL.createObjectURL(blob)
    const a = document.createElement("a")
    a.href = url
    a.download = `queue_faxnumbers.csv`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  }

  return (
    <div style={{ display: "flex", justifyContent: "center" }}>
      {loading ? (
        <div className="loading-container-fax-list">
          <div className="loading-message">Loading...</div>
        </div>
      ) : (
        <div
          className="faxes-content"
          style={{ textAlign: "center", marginTop: "20px", width: "100%" }}
        >
          <div
            className="search-container"
            style={{
              width: "100%",
              alignItems: "end",
              display: "flex",
              justifyContent: "space-between",
            }}
          >
            <div className="setting-note">
              <div>
                {/* <input
                  type="text"
                  placeholder={`Search Queues`}
                  value={searchText}
                  onChange={(e) => {
                    setSearchText(e.target.value)
                    setCurrentPage(1)
                  }}
                /> */}

                <SearchInputWrapper
                  value={searchText}
                  placeholder="Search Queues"
                  onChange={(val) => {
                    setSearchText(val)
                    setCurrentPage(1)
                  }}
                  onClear={() => {
                    setSearchText("")
                    setCurrentPage(1)
                  }}
                />
              </div>
            </div>

            <div style={{ display: "flex" }}>
              <button
                className="faxes-button"
                onClick={() => handleExportCsv()}
                style={{ height: "28px" }}
              >
                <span>
                  {" "}
                  <FileSpreadsheet size={16} color="white" />{" "}
                </span>
                <span>Export</span>
              </button>

              <button
                className="faxes-button"
                onClick={() => setShowPopup(true)}
                style={{ marginBottom: "5px" }}
              >
                + Add Queue
              </button>
            </div>
          </div>
          <div className="table-container">
            <table className="faxes-table">
              <thead>
                <tr>
                  <th className="faxes-th" style={{ width: "200px" }}>
                    Queue Name
                  </th>
                  <th className="faxes-th">Fax Number</th>
                  <th
                    className="faxes-th"
                    style={{ textAlign: "center", width: "100px" }}
                  >
                    Action
                  </th>
                </tr>
              </thead>
              <tbody>
                {currentQueues.length > 0 ? (
                  currentQueues.map((queue) => (
                    <tr
                      key={queue.queueName}
                      className={`fax-item${activeRowId && activeRowId === queue.queueName ? " fax-action-active" : ""}`}
                      style={{ cursor: "unset" }}
                      onMouseEnter={() => {
                        if (activeRowId && activeRowId !== queue.queueName)
                          setActiveRowId(null)
                      }}
                    >
                      <td className="faxes-td">{queue.queueName}</td>
                      <td className="faxes-td">
                        {formatFaxNumber(queue.faxNumber)}
                        <div
                          style={{
                            display: "flex",
                            flexWrap: "wrap",
                            gap: "0px",
                          }}
                        >
                          {queue.associations?.map((ass, index) => (
                            <span key={ass.faxNumber} className="fax-tag">
                              {ass.faxNumber}
                              <span
                                className="delete-tag"
                                onClick={() => {
                                  setActiveRowId(queue.queueName)
                                  setQueueIdToDelete({
                                    queueName: queue.queueName,
                                    faxNumber: ass.faxNumber,
                                    deleteFax: true,
                                  })
                                }}
                              >
                                <X size={14} />
                              </span>
                            </span>
                          ))}
                        </div>
                      </td>

                      <td className="faxes-td" style={{ textAlign: "center" }}>
                        <span
                          style={{ cursor: "pointer" }}
                          onClick={() => {
                            setActiveRowId(queue.queueName)
                            setQueueToEdit(queue)
                          }}
                        >
                          <SquarePen size="18" color="black" />
                        </span>
                        <span
                          style={{ cursor: "pointer", marginLeft: "10px" }}
                          onClick={() => {
                            setActiveRowId(queue.queueName)
                            setQueueIdToDelete(queue)
                          }}
                        >
                          <Trash2 size="18" color="red" />
                        </span>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td
                      colSpan="100%"
                      style={{
                        textAlign: "center",
                        padding: "10px",
                        border: "1px solid #ccc",
                      }}
                    >
                      No data found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          {filteredQueues.length > 0 && (
            <div
              className="faxes-pagination"
              style={{
                marginTop: "8px",
                display: "flex",
                justifyContent: "end",
              }}
            >
              {filteredQueues.length > 0 && (
                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  rowsPerPage={rowsPerPage}
                  totalRows={filteredQueues.length}
                  onPageChange={(page) => setCurrentPage(page)}
                  onRowsPerPageChange={(count) => {
                    setRowsPerPage(count)
                    setCurrentPage(1) // Reset to first page on change
                  }}
                />
              )}
            </div>
          )}
        </div>
      )}

      {(showPopup || queueToEdit) && (
        <QueueForm
          onSave={handleAddQueue}
          onClose={() => {
            setShowPopup(false)
            setQueueToEdit(null)
          }}
          queueToEdit={queueToEdit}
          actionLoading={actionLoading}
          queues={queues.map((q) => q.queueName)}
        />
      )}

      {queueIdToDelete && (
        <DeleteConfirmationPopup
          onConfirm={handleDeleteQueue}
          onCancel={() => setQueueIdToDelete(null)}
          actionLoading={actionLoading}
          queueIdToDelete={queueIdToDelete}
        />
      )}
    </div>
  )
}

const QueueForm = ({ onClose, onSave, queueToEdit, actionLoading, queues }) => {
  const [queueName, setQueueName] = useState("")
  const [faxNumber, setFaxNumber] = useState("")
  const [errorMessage, setErrorMessage] = useState("")
  const [mode, setMode] = useState("new")

  // Load initial data if editing
  useEffect(() => {
    if (queueToEdit) {
      setQueueName(queueToEdit?.queueName)
      setFaxNumber(queueToEdit?.faxNumber)
    }
  }, [queueToEdit])

  const handleSubmit = (e) => {
    e.preventDefault()

    if (!queueName.trim()) {
      setErrorMessage("Queue Name is required.")
      return
    }

    const specialCharRegex = /^[A-Za-z0-9_ -]+$/
    if (!specialCharRegex.test(queueName.trim())) {
      setErrorMessage(
        "Queue Name can contain only letters, numbers, hyphens (-), underscores (_) and spaces.",
      )
      return
    }

    if (!queueToEdit) {
      if (!faxNumber) {
        setErrorMessage("Fax Number is required")
        return
      } else {
        //const isValidated = validateFaxNumber(faxNumber.trim())
        // if (!isValidated) {
        //   setErrorMessage("Fax Number is incorrect")
        //   return
        // }

        const errorMsg = validateFaxNumberOnlyDigit(faxNumber.trim())

        if (errorMsg) {
          setErrorMessage(errorMsg)
          return
        }
      }
    }

    let params = null
    if (queueToEdit) {
      params = {
        oldQueueName: queueToEdit?.queueName.trim(),
        newQueueName: queueName.trim(),
        isEdit: true,
      }
    } else {
      params = {
        queueName: queueName.trim(),
        faxNumber: prepareFinalFaxNumber(faxNumber),
        isEdit: false,
      }
    }

    if (
      queueToEdit &&
      ["queueName"].every((field) => params[field] === queueToEdit[field])
    ) {
      setErrorMessage("Queue Name has not been changed")
      return
    }

    onSave(params)
  }

  const optionQueues = [
    {
      value: "",
      label: "Select Queue",
    },
    ...queues.map((n) => ({
      value: n,
      label: n,
    })),
  ]

  return (
    <div className="popup-overlay">
      <div className="popup">
        <div className="checkbox-popup-header">
          <h3 className="popup-title">
            {queueToEdit ? `Update` : `Add`}{" "}
            {mode === "existing" ? "Fax Number" : "Queue"}
          </h3>
          <button
            type="button"
            onClick={onClose}
            style={{
              background: "transparent",
              border: "none",
              fontSize: 24,
              cursor: "pointer",
            }}
          >
            ×
          </button>
        </div>

        <form className="popup-group" onSubmit={handleSubmit}>
          {/* TOGGLE */}
          {queues.length > 0 && !queueToEdit && (
            <div
              className="form-group"
              style={{
                display: "flex",
                alignItems: "center",
                gap: "20px",
                marginBottom: "10px",
              }}
            >
              <label
                style={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: "6px",
                  margin: 0,
                  cursor: "pointer",
                }}
              >
                <input
                  type="radio"
                  style={{ width: "15px" }}
                  name="queueMode"
                  value="new"
                  checked={mode === "new"}
                  onChange={() => {
                    setMode("new")
                    setQueueName("")
                  }}
                />
                <span>New Queue</span>
              </label>

              <label
                style={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: "6px",
                  margin: 0,
                  cursor: "pointer",
                }}
              >
                <input
                  type="radio"
                  style={{ width: "15px" }}
                  name="queueMode"
                  value="existing"
                  checked={mode === "existing"}
                  onChange={() => {
                    setMode("existing")
                    setQueueName("")
                  }}
                />
                <span>Existing Queue</span>
              </label>
            </div>
          )}

          {/* QUEUE INPUT / SELECT */}
          <div className="form-group">
            <label htmlFor="queueName">
              Queue Name: <span className="mandatory">*</span>
            </label>

            {mode === "new" ? (
              <input
                type="text"
                id="queueName"
                value={queueName}
                style={{ height: "38px" }}
                onChange={(e) => {
                  setQueueName(e.target.value)
                  setErrorMessage("")
                }}
              />
            ) : (
              <Select
                id="queueName"
                className="basic-single"
                classNamePrefix="select"
                value={optionQueues.find(
                  (option) => option.value === queueName,
                )}
                onChange={(selectedOption) => {
                  setQueueName(selectedOption.value)
                  setErrorMessage("")
                }}
                options={optionQueues}
                menuPortalTarget={document.body}
                menuPosition="fixed"
                styles={{
                  control: (base) => ({
                    ...base,
                    borderRadius: 0,
                    textAlign: "left",
                  }),
                  option: (base) => ({
                    ...base,
                    textAlign: "left",
                  }),
                  singleValue: (base) => ({
                    ...base,
                  }),
                  menuPortal: (base) => ({
                    ...base,
                    zIndex: 9999,
                  }),
                }}
                placeholder="Select User..."
              />
            )}
          </div>

          {/* FAX NUMBER */}
          {!queueToEdit && (
            <div className="form-group">
              <label htmlFor="faxNumber">
                Fax Number: <span className="mandatory">*</span>
              </label>
              <input
                type="text"
                id="faxNumber"
                value={faxNumber}
                onChange={(e) => {
                  setFaxNumber(e.target.value)
                  setErrorMessage("")
                }}
              />
            </div>
          )}

          {errorMessage && <div className="error-message">{errorMessage}</div>}

          <div className="popup-buttons">
            <button
              className="popup-button cancel"
              type="button"
              onClick={onClose}
            >
              Cancel
            </button>

            <button className="popup-button save" type="submit">
              {actionLoading ? "Saving ..." : "Save"}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

const DeleteConfirmationPopup = ({
  onConfirm,
  onCancel,
  actionLoading,
  queueIdToDelete,
}) => (
  <div className="popup-overlay">
    <div className="popup" style={{ padding: "20px", maxWidth: "700px" }}>
      {actionLoading ? (
        <div>Processing...</div>
      ) : (
        <>
          <div>
            <p style={{ fontWeight: "bold", marginBottom: "10px" }}>
              {queueIdToDelete.deleteFax
                ? `Are you sure you want to delete the fax number (${queueIdToDelete.faxNumber})?`
                : `Are you sure you want to delete the queue (${queueIdToDelete.queueName})?`}
            </p>

            <div
              style={{
                fontSize: "13px",
                lineHeight: "0.1",
                cursor: "unset",
                marginTop: "25px",
              }}
            >
              <ul
                style={{
                  paddingLeft: "18px",
                  marginBottom: "10px",
                  cursor: "unset",
                }}
              >
                <li>
                  {queueIdToDelete.deleteFax
                    ? `Deleting this fax number will permanently removed.`
                    : `Deleting this queue will permanently removed.`}
                </li>
                <li>
                  {queueIdToDelete.deleteFax
                    ? `Fax number from this queue and any faxes copied to subfolders will be removed as well.`
                    : `All associated fax numbers and any faxes copied to subfolders will be removed as well.`}
                </li>
                {/* <li>
                  This action will permanently delete all associated subfolders
                  from the Queue and copied fax records from NETID subfolders.
                </li> */}
              </ul>

              <p
                style={{
                  color: "#d32f2f",
                  fontWeight: "bold",
                  marginTop: "30px",
                  marginBottom: "30px",
                }}
              >
                <TriangleAlert size={18} fill="yellow" stroke="black" style={{ marginRight: "5px", verticalAlign: "middle" }} />
                This operation is irreversible and data cannot be recovered
                once deleted.
              </p>
            </div>
          </div>
          <div className="popup-buttons">
            <button className="cancel" onClick={onCancel}>
              Cancel
            </button>
            <button className="delete" onClick={onConfirm}>
              Delete
            </button>
          </div>
        </>
      )}
    </div>
  </div>
)

export default QueueToFaxNumberAssociation
